import { http } from "./http";

/**
 * Busca nome do funil e mapa stageId -> nome das etapas (uma chamada de funil + uma de estágios por funil).
 * @param {string} companyId
 * @param {string} funnelId UUID
 * @returns {Promise<{ funnelName: string | null, stageNamesById: Record<string, string> }>}
 */
export async function fetchFunnelAndStageNames(companyId, funnelId) {
  if (!funnelId || String(funnelId).trim() === "") {
    return { funnelName: null, stageNamesById: {} };
  }
  const headers = { "X-Company-Id": String(companyId) };
  const fid = String(funnelId).trim();

  const [funnelRes, stagesRes] = await Promise.all([
    http.get(`/api/metadata/funil/${fid}`, { headers }).catch(() => ({ data: null })),
    http.get(`/api/metadata/pipelines/${fid}/stages`, { headers }).catch(() => ({ data: [] })),
  ]);

  const funnelName =
    funnelRes?.data?.name != null && String(funnelRes.data.name).trim() !== ""
      ? String(funnelRes.data.name).trim()
      : null;

  const stageNamesById = {};
  const stages = Array.isArray(stagesRes?.data) ? stagesRes.data : [];
  stages.forEach((s) => {
    if (s?.id != null && s?.name != null && String(s.name).trim() !== "") {
      stageNamesById[String(s.id)] = String(s.name).trim();
    }
  });

  return { funnelName, stageNamesById };
}

/**
 * Para uma lista de oportunidades com funnelId (e opcionalmente stageId), carrega nomes de funis e estágios.
 * Agrupa por funnelId para minimizar requisições.
 *
 * @param {string} companyId
 * @param {Array<{ funnelId?: string, stageId?: string }>} opportunities
 * @returns {Promise<{ funnelNameById: Record<string, string>, stageNameByFunnelStage: Record<string, string> }>}
 */
export async function enrichOpportunitiesFunnelStageNames(companyId, opportunities) {
  const funnelNameById = {};
  const stageNameByFunnelStage = {};

  const uniqueFunnelIds = [
    ...new Set(
      (opportunities || [])
        .map((o) => (o?.funnelId != null ? String(o.funnelId).trim() : ""))
        .filter((id) => id !== "")
    ),
  ];

  await Promise.all(
    uniqueFunnelIds.map(async (fid) => {
      try {
        const { funnelName, stageNamesById } = await fetchFunnelAndStageNames(companyId, fid);
        if (funnelName) {
          funnelNameById[fid] = funnelName;
        }
        Object.entries(stageNamesById).forEach(([stageId, name]) => {
          stageNameByFunnelStage[`${fid}|${stageId}`] = name;
        });
      } catch {
        // Mantém mapas sem entrada; UI continua mostrando só UUID
      }
    })
  );

  return { funnelNameById, stageNameByFunnelStage };
}
